// ONT reasoning for the ONT list and detail pages. Pure, so
// `bun run src/lib/ontstate.check.ts` can check it.
import { rxLevel, STALE_POLLS, type RxLevel } from "./olthealth";
import type { SignalBands } from "./signal";
import type { ONT } from "./types";
import type { WANConnection } from "./api";

/**
 * Where the ONT is RIGHT NOW. Kept apart from its last outage on purpose: an
 * online ONT whose last drop was a dying gasp is online, not "power failure".
 *  stale   the OLT still lists it Online, but has not seen it for 3 polls.
 */
export type OntLive = "online" | "offline" | "missing" | "stale" | "unknown";

export function ontLive(o: Pick<ONT, "run_state" | "last_seen_at">, pollS: number, nowMs: number): OntLive {
  if (!o.run_state) return "unknown";
  if (o.run_state === "Missing") return "missing";
  if (o.run_state !== "Online") return "offline";
  if (o.last_seen_at && (nowMs - Date.parse(o.last_seen_at)) / 1000 > pollS * STALE_POLLS) return "stale";
  return "online";
}

/** Human cause for the OLT's down reason. Dying gasp is evidence, not proof, of a power loss. */
export type CauseKind = "dyingGasp" | "los" | "other";
export function causeKind(raw: string | null | undefined): CauseKind | null {
  if (!raw || !raw.trim() || raw.trim() === "--") return null;
  if (/gasp/i.test(raw)) return "dyingGasp";
  if (/^\s*(LOS|LOSi|LOFi|LOAMi)\b|loss of signal/i.test(raw)) return "los";
  return "other";
}

export type Outage = {
  kind: CauseKind | null;
  raw: string | null;
  startedAt: string | null;
  /** When it ended; null while it is still going on. */
  endedAt: string | null;
  ongoing: boolean;
  durationMs: number | null;
};

/**
 * The most recent outage, as history. Ongoing when the ONT is down now;
 * otherwise resolved, with a duration only if both ends are known and ordered.
 */
export function lastOutage(
  o: Pick<ONT, "run_state" | "last_down_cause" | "last_down_at" | "last_up_at" | "last_seen_at">, nowMs: number,
): Outage | null {
  const kind = causeKind(o.last_down_cause);
  const ongoing = !!o.run_state && o.run_state !== "Online";
  const startedAt = o.last_down_at ?? (ongoing ? o.last_seen_at : null) ?? null;
  if (!kind && !startedAt) return null;
  const endedAt = ongoing ? null : o.last_up_at && startedAt && Date.parse(o.last_up_at) >= Date.parse(startedAt) ? o.last_up_at : null;
  const durationMs = !startedAt ? null : ongoing ? nowMs - Date.parse(startedAt) : endedAt ? Date.parse(endedAt) - Date.parse(startedAt) : null;
  return { kind, raw: o.last_down_cause?.trim() || null, startedAt, endedAt, ongoing, durationMs };
}

/**
 * The customer's router as seen by the ACS. GenieACS only knows the CPE by its
 * informs, so a quiet CPE is "silent", not "offline"; when the ONT itself is
 * down the CPE is unreachable by definition, so its state is unknown.
 */
export type CpeState = "online" | "silent" | "unknown" | "none";
// ponytail: fixed 15 min; the per-CPE periodic inform interval would replace it if GenieACS exposed it.
export const CPE_FRESH_MS = 15 * 60_000;

export function cpeState(cpe: { last_inform_at: string | null } | null | undefined, ontUp: boolean, nowMs: number): CpeState {
  if (!cpe) return "none";
  if (!ontUp || !cpe.last_inform_at) return "unknown";
  return nowMs - Date.parse(cpe.last_inform_at) <= CPE_FRESH_MS ? "online" : "silent";
}

/**
 * The ACS management connection (TR-069 / MGMT VLAN) keeps the CPE reachable
 * but carries no customer traffic: its being up says nothing about whether the
 * customer can browse. Matched on the connection's name.
 */
export const isMgmt = (c: Pick<WANConnection, "name">) => /tr-?069|mgmt|manage|acs/i.test(c.name ?? "");

/**
 * WAN summary from the customer's own services (management links only count
 * when there is nothing else): up if one is connected; unknown when the CPE
 * reports no state.
 */
export type WanState = "up" | "down" | "unknown" | "none";
export function wanState(conns: Pick<WANConnection, "enabled" | "status" | "name">[] | undefined): WanState {
  if (!conns) return "unknown";
  const enabled = conns.filter((c) => c.enabled !== false);
  const service = enabled.filter((c) => !isMgmt(c));
  const live = service.length ? service : enabled;
  if (!live.length) return "none";
  if (live.some((c) => /^connected$/i.test(c.status))) return "up";
  if (live.every((c) => !c.status)) return "unknown";
  return "down";
}
export function connState(c: Pick<WANConnection, "enabled" | "status">): "up" | "down" | "disabled" | "unknown" {
  if (c.enabled === false) return "disabled";
  if (!c.status) return "unknown";
  return /^connected$/i.test(c.status) ? "up" : "down";
}

/**
 * History points ready to draw: a gap (null) wherever samples stop for longer
 * than maxGapMs, so an offline stretch is a hole, not a straight line across
 * it; and no reading of exactly 0 dBm, which is a missing value from the
 * collector, never a real receive level.
 */
export function withGaps(points: { ts: string; value: number }[], maxGapMs: number): { ts: number; value: number | null }[] {
  const out: { ts: number; value: number | null }[] = [];
  let prev: number | null = null;
  for (const p of points) {
    const ts = Date.parse(p.ts);
    if (prev !== null && ts - prev > maxGapMs) out.push({ ts: prev + 1, value: null });
    out.push({ ts, value: Number.isFinite(p.value) && p.value !== 0 ? p.value : null });
    prev = ts;
  }
  return out;
}

/** Optical-history summary over valid readings only. */
export function stats(points: { value: number | null }[]) {
  const v = points.map((p) => p.value).filter((x): x is number => x !== null);
  if (!v.length) return null;
  return { min: Math.min(...v), max: Math.max(...v), avg: v.reduce((a, b) => a + b, 0) / v.length, last: v[v.length - 1] };
}

/**
 * Default operational order for the ONT list: service down first, then the
 * optics that are about to take it down, then data we cannot trust, then fine.
 */
export function triageRank(o: Pick<ONT, "run_state" | "rx_power_dbm" | "last_seen_at"> & { critical_alerts?: number },
  bands: SignalBands, pollS: number, nowMs: number): number {
  const live = ontLive(o, pollS, nowMs);
  if (live === "offline" || live === "missing") return 0;
  const lvl: RxLevel = rxLevel(o.rx_power_dbm, bands);
  if (lvl === "critical" || (o.critical_alerts ?? 0) > 0) return 1;
  if (lvl === "weak") return 2;
  if (live === "stale" || live === "unknown") return 3;
  return 4;
}
